import React, { useState, useEffect } from 'react';
import "./App.css";
import "./styles/global.css";
import SignIn from "./pages/sign-in/SignIn";
import SignUp from "./pages/sign-up/SignUp";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import AppointmentsTable from "./pages/appointments-table/AppointmentsTable";
import BookAnAppointment from "./pages/book-an-appointment/BookAnAppointment";
import BookAppointment from './pages/appointment/BookAppointment';
import Home from './pages/home/Home';
import { v2 } from "./api/v2";
import Profile from './pages/profile/Profile';
import Layout from "./components/layout/Layout";
import * as _ from "lodash";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import Slide from '@mui/material/Slide';
import axios from 'axios';
import Utils from './utils/utils';
import { resetSessionVariables } from './services/LogOutService';
import { enforcePatientTenant, tenantFromPath } from './utils/tenant-guard';
import { ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref,
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function TransitionLeft(props: any) {
  return <Slide {...props} direction="left" />;
}

// NotFound page (friendly error)
const NotFound = () => (
  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh" textAlign="center" p={2}>
    <Typography variant="h4" color="error" gutterBottom>
      404 - Page Not Found
    </Typography>
    <Typography variant="body1" gutterBottom>
      The page you are looking for does not exist or is invalid.
    </Typography>
    {/*<Button variant="contained" color="primary" href="/">
      Go Home
    </Button>*/}
  </Box>
);

// Wrapper for validating home/:referenceCode. The referenceCode IS the v2
// tenant_code (e.g. "manoshanti_bengaluru") — dynamic per clinic and validated
// server-side (the v2 API 404s an unknown tenant), so we only require it to be
// present rather than gating on a hardcoded list.
const ValidHomeRoute = () => {
  const { referenceCode } = useParams<{ referenceCode: string }>();

  if (!referenceCode) {
    return <Navigate to="/404" replace />;
  }

  return <Layout children={<Home />} />;
};

// Shown when a clinic/group hasn't enabled online web booking (or hasn't
// configured the SMS/email needed to run it). The patient can't proceed; they're
// pointed at the clinic admin.
const WebBookingDisabled = ({ brand }: { brand: string | null }) => (
  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh" textAlign="center" p={3}>
    <Typography variant="h5" gutterBottom>Online booking isn't available</Typography>
    <Typography variant="body1" sx={{ maxWidth: 460, color: "text.secondary" }}>
      {brand ? `${brand} hasn't enabled online appointment booking yet.` : "This clinic hasn't enabled online appointment booking yet."}
      {" "}Please contact the clinic / workspace administrator to enable it.
    </Typography>
  </Box>
);

// On load (and on tenant switch) check whether the tenant/group in the URL has
// web booking enabled in v2; if not, show the notice instead of the app. The
// check is keyed on the URL's tenant/group code. Fail-open on errors so a
// transient backend hiccup never hard-blocks a clinic that's actually enabled
// (the booking endpoints still enforce server-side).
const WebBookingGate = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const tenant = tenantFromPath(location.pathname);
  const [status, setStatus] = useState<"checking" | "ok" | "blocked">(tenant ? "checking" : "ok");
  const [brand, setBrand] = useState<string | null>(null);

  useEffect(() => {
    if (!tenant) { setStatus("ok"); return; }
    let cancelled = false;
    setStatus("checking");
    v2.getPaymentConfig(tenant)
      .then((cfg) => {
        if (cancelled) return;
        setBrand(cfg.brand_name);
        setStatus(cfg.web_booking_enabled ? "ok" : "blocked");
      })
      .catch(() => { if (!cancelled) setStatus("ok"); });
    return () => { cancelled = true; };
  }, [tenant]);

  if (status === "checking") {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="100vh">
        <Typography color="text.secondary">Loading…</Typography>
      </Box>
    );
  }
  if (status === "blocked") return <WebBookingDisabled brand={brand} />;
  return <>{children}</>;
};

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const referenceCode = new URLSearchParams(location.search).get('referenceCode');

  const isAuthenticatedStore = localStorage.getItem("isAuthenticated") || false;
  const patientDetailsStore = localStorage.getItem("patientDetails") || null;

  const [isAuthenticated, setAuthenticated] = useState(isAuthenticatedStore);
  const [patientDetails, setpatientDetails] = useState(patientDetailsStore);

  const [isUnAuthorized, setIsUnAuthorized] = useState(JSON.parse(
    localStorage.getItem("isUnAuthorized") || null
  ));

  useEffect(() => {
    if (referenceCode)
      localStorage.setItem("referenceCode", referenceCode ?? null);
  }, [referenceCode])

  // Client-side tenant switch (SPA nav between clinics without a full reload):
  // drop a session that belongs to a different clinic and reflect the logout in
  // React state so the UI updates immediately (the bootstrap guard in index.tsx
  // covers the full-reload / address-bar-edit case).
  useEffect(() => {
    if (enforcePatientTenant(location.pathname)) {
      setAuthenticated(false);
      setpatientDetails(null);
    }
  }, [location.pathname])

  useEffect(() => {
    if (isAuthenticated)
      checkIsToeknActive();
  }, [isAuthenticated])

  const checkIsToeknActive = async () => {
    // v2: validate the patient JWT via /v1/public/patient/me. On 401/expired,
    // clear the session (mirrors the legacy is-token-active behaviour).
    await v2.me(referenceCode || undefined).catch(() => {
      resetSessionVariables();
    });
  }
  const onLoginSuccess = (response: any) => {
    setAuthenticated(true);
    localStorage.setItem("authToken", response?.access_token ?? null);
    delete response.access_token;
    setpatientDetails(response);
    localStorage.setItem("patientDetails", JSON.stringify(response));
    localStorage.setItem("isAuthenticated", JSON.stringify(true));
  };

  const handleClose = (event: any) => {
    localStorage.setItem("isUnAuthorized", JSON.stringify(false));
    setIsUnAuthorized(Boolean(false));
  };

  return (
    <div className="App">
      {isUnAuthorized &&
        <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} TransitionComponent={TransitionLeft} open={isUnAuthorized} autoHideDuration={6000} onClose={handleClose}>
          <Alert onClose={handleClose} severity="error" sx={{ width: '100%' }}>
            Signature verification failed. Please relogin again.
          </Alert>
        </Snackbar>
      }
      <ToastContainer />
      <WebBookingGate>
      <Routes>
        <Route
          path={"/"}
          element={
            (isAuthenticated === true) ? (
              <Navigate to={`/appointment-list/${referenceCode}`} />
            ) : (
              <Navigate to={`/home/${referenceCode}`} />
            )
          }
        />
        <Route
          path="/appointment-list/:referenceCode"
          element={<Layout children={<AppointmentsTable />}></Layout>}
        />
        {/* Home route with validation */}
        <Route
          path="/home/:referenceCode"
          element={<ValidHomeRoute />}
        />
        <Route
          path="/profile/:heCode/:userId"
          element={<Layout children={<Profile />}></Layout>}
        />
        <Route
          path={`/sign-in/${referenceCode}`}
          element={
            isAuthenticated ? (
              <Navigate to="/" />
            ) : (
              <Layout children={
                <SignIn
                  onLoginSuccess={(response: any) => {
                    onLoginSuccess(response);
                  }}
                />
              } />
            )
          }
        />
        <Route
          path="/sign-in/:referenceCode"
          element={
            <Layout children={<SignIn
              onLoginSuccess={(response: any) => {
                onLoginSuccess(response);
              }}
            />}></Layout>
          }
        />
        <Route
          path="/book-an-appointment"
          element={
            _.isEmpty(isAuthenticated) ? (
              <Navigate to={`/home/${referenceCode}`} />
            ) : (
              <Layout children={<BookAppointment onLoginSuccess={{}} />}></Layout>
            )
          }
        />
        <Route
          path="/reschedule-appointment/:heId/:apptId"
          element={
            _.isEmpty(isAuthenticated) ? (
              <Navigate to={`/home/${referenceCode}`} />
            ) : (
              <Layout children={<BookAppointment onLoginSuccess={{}} />}></Layout>
            )
          }
        />
        <Route
          path="/book-an-appointment/:heCode"
          element={<Layout children={<BookAppointment onLoginSuccess={onLoginSuccess} />}></Layout>}
        />
        <Route
          path="/book-an-appointment/:heCode/:userId"
          element={<Layout children={<BookAppointment onLoginSuccess={onLoginSuccess} />}></Layout>}
        />
        <Route
          path="/book-appointment"
          element={
            _.isEmpty(isAuthenticated) ? (
              <Navigate to={`/home/${referenceCode}`} />
            ) : (
              <Layout children={<BookAnAppointment />}></Layout>
            )
          }
        />
        <Route
          path="/sign-up/:referenceCode"
          element={isAuthenticated ? <Navigate to={"/home/" + referenceCode} /> :
            <Layout children={<SignUp />}></Layout>}
        />

        {/* Error route */}
        <Route path="/404" element={<NotFound />} />
        <Route path="*" element={<Navigate to="/404" />} />
      </Routes>
      </WebBookingGate>
    </div>
  );
};
export default App;
